// Stromzaehlerablesung: App-Rahmen und Zustand in einfachem JavaScript (ohne React).
// Ein veränderlicher Zustand `s`; jede Änderung ruft `render()` (gesammelt, ein Zeichnen je Durchlauf). Was früher Effekte waren, sind hier ausdrückliche Funktionen,
// die an der Stelle aufgerufen werden, an der sich die Daten ändern (`load`, `select`, `setPhoto` …). Die Ansichten der Reiter stehen in `plain/*.js`, der Rahmen in `shell.ts`.
import * as teams from '@microsoft/teams-js';
import { ConsentRequiredError, createOcrTest, createStructure, deleteOcrTest, downloadExport, fetchCounts, fetchOcrTestPhoto, fetchOcrTests, fetchReadingPhoto, fetchReadings, getMe, getSettings, importFile, markChecked, ocrBackfillRun, ocrBackfillStatus, readMeterOcr, saveSettings, setupOcrTests, submitReading, type ReadingCounts, type SettingsInfo } from './api';
import { EXPORT_COLUMNS, EXPORT_PRESETS, EXPORT_SCOPES, type ExportScope } from './exportColumns';
import { getAccountSummary, getAuthError, getAuthLog, getRememberedAccountName, initializeAuth, isLoginInProgress, isSignedIn, isSigningIn, signIn, signOut } from './auth';
import { errorText } from './brand';
import { cacheReadings, enqueue, getCachedReadings, queueCount } from './db';
import { appConfig } from './generated/appConfig';
import { releaseNotes } from './releaseNotes';
import { addressKey, distanceKm, formatDistance, getPosition, resolveCoordinates, type GeoPoint } from './geo';
import { normalizePhoto } from './image';
import { checkOfflineReadiness, rememberCacheInfo, type OfflineReadiness } from './offline';
import { siteFromInput, siteToInput } from './site';
import { synchronize } from './sync';
import { mount, type Actions } from './plain/kit.js';
import { infoActions, infoView } from './plain/info.js';
import { settingsActions, settingsView } from './plain/settings.js';
import { adminActions, adminView, auswertungView } from './plain/admin.js';
import { readingsActions, readingsView } from './plain/readings.js';
import { createOcrTests } from './plain/ocr-tests.js';
import { appView, type ShellState, type TabId, type VersionCheck } from './shell';
import type { AdminActions, AdminState, InfoActions, InfoState, ReadingsActions, ReadingsState, SettingsActions, SettingsState } from './view-types';
import type { HostMode, ImportResult, OcrBackfillCounts, OcrMode, QueueItem, Reading, ReadingStatus, UserInfo } from './types';

const yearNow = new Date().getFullYear();
/** So viele Karten zeigt die Ablesungsliste zuerst; beim Scrollen kommen weitere dazu. */
const PAGE_SIZE = 50;
const PREFS_KEY = 'pv-ansicht';
const INSTALL_HINT_KEY = 'pv-install-hint-hidden';
type SortMode = 'standard' | 'distance';
type InstallPromptEvent = Event & { prompt: () => Promise<void>; userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }> };

const statusFilters: Array<{ value: ReadingStatus | ''; label: string }> = [
  { value: 'Offen', label: 'Offen' }, { value: 'In Bearbeitung', label: 'In Bearbeitung' }, { value: 'Zu prüfen', label: 'Zu prüfen' },
  { value: 'Abgelesen', label: 'Abgelesen' }, { value: '', label: 'Alle' }
];

const yearOptions = (selected: number): number[] => {
  const list: number[] = [];
  for (let y = yearNow + 1; y >= yearNow - 5; y--) list.push(y);
  if (!list.includes(selected)) list.push(selected);
  return list.sort((a, b) => b - a);
};

/** Zuletzt gewählter Status und zuletzt gewählte Sortierung (nur auf diesem Gerät gespeichert). */
function readPrefs(): { status?: ReadingStatus | ''; sortMode?: SortMode } {
  try {
    const raw = JSON.parse(localStorage.getItem(PREFS_KEY) ?? '{}') as { status?: unknown; sortMode?: unknown };
    const known = statusFilters.some(filter => filter.value === raw.status);
    return { status: known ? raw.status as ReadingStatus | '' : undefined, sortMode: raw.sortMode === 'distance' || raw.sortMode === 'standard' ? raw.sortMode : undefined };
  } catch { return {}; }
}

function writePrefs(prefs: { status: ReadingStatus | ''; sortMode?: SortMode }): void {
  try { localStorage.setItem(PREFS_KEY, JSON.stringify(prefs)); } catch { /* Speicher gesperrt: Auswahl wird nicht gemerkt */ }
}

/** Beschriftung eines Statusknopfs, mit der Anzahl, sobald sie bekannt ist („Offen (9)“, „Alle (120)“). */
function filterLabel(filter: { value: ReadingStatus | ''; label: string }, counts?: ReadingCounts): string {
  if (!counts) return filter.label;
  return `${filter.label} (${filter.value === '' ? counts.total : counts.byStatus[filter.value] ?? 0})`;
}

function filterCount(filter: { value: ReadingStatus | '' }, counts?: ReadingCounts): number | undefined {
  if (!counts) return undefined;
  return filter.value === '' ? counts.total : counts.byStatus[filter.value] ?? 0;
}

/** Einheit des Zählerstands nach Energieart: Gas und Wasser in m³, alles andere (Strom, PV-Strom, Wärmepumpenstrom, ohne Angabe) in kWh. */
function zaehlerEinheit(energie?: string): string { return energie === 'Gas' || energie === 'Wasser' ? 'm³' : 'kWh'; }

/** Zählerstand: Ziffern, optional Dezimalstellen mit Komma oder Punkt. Gibt undefined zurück, wenn der Wert nicht verwendbar ist. */
function parseReading(text: string): number | undefined {
  const trimmed = text.trim();
  if (!/^\d+(?:[.,]\d+)?$/.test(trimmed)) return undefined;
  const numeric = Number(trimmed.replace(',', '.'));
  return Number.isFinite(numeric) ? numeric : undefined;
}

function formatNumber(value: number): string { return new Intl.NumberFormat('de-DE', { maximumFractionDigits: 3 }).format(value); }

function badgeClass(status: ReadingStatus | undefined): string {
  if (status === 'Offen') return 'warnung';
  if (status === 'Zu prüfen') return 'fehler';
  if (status === 'Abgelesen') return 'erfolg';
  return '';
}

function modeLabel(mode: HostMode): string {
  return ({ teams: 'Microsoft Teams', outlook: 'Microsoft Outlook', pwa: 'installierte PWA', browser: 'Webbrowser' })[mode];
}

function searchText(item: Reading): string {
  return [item.Strasse, item.Ort, item.Zaehlernummer, item.OrtZaehler, item.Zweck, item.Hinweise, item.Title]
    .filter((part): part is string => typeof part === 'string' && part.length > 0).join(' ').toLowerCase();
}

/** „Gespeichert von … am …“ zum vorhandenen Foto (Name und Zeit aus der Liste, soweit vorhanden). */
function savedPhotoInfo(reading: Reading): string {
  const when = reading.AbgelesenAm ? new Date(reading.AbgelesenAm) : undefined;
  const date = when && !Number.isNaN(when.getTime()) ? when.toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' }) : '';
  return `Gespeichert${reading.AbgelesenVon ? ` von ${reading.AbgelesenVon}` : ''}${date ? ` am ${date}` : ''}.`;
}

function isIos(): boolean {
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) || (ua.includes('Macintosh') && navigator.maxTouchPoints > 1);
}

/** Safari auf iPhone oder iPad (nicht Edge, Chrome, Firefox oder Opera): nur dort lässt sich die Seite zum Home-Bildschirm hinzufügen. */
function isSafariIos(): boolean {
  return isIos() && /Safari/.test(navigator.userAgent) && !/CriOS|EdgiOS|FxiOS|OPiOS|GSA/.test(navigator.userAgent);
}

function download(fileName: string, text: string): void {
  const url = URL.createObjectURL(new Blob([text], { type: 'application/json' }));
  const a = document.createElement('a');
  a.href = url; a.download = fileName; a.click();
  URL.revokeObjectURL(url);
}

interface OcrHint { status: 'loading' | 'done' | 'error'; value?: number; confidence?: number; message?: string }

const s = {
  ready: false, loaded: false, mode: 'browser' as HostMode, user: undefined as UserInfo | undefined, signedIn: false,
  items: [] as Reading[], year: yearNow, status: (readPrefs().status ?? 'Offen') as ReadingStatus | '', query: '',
  selected: undefined as Reading | undefined, value: '', photo: undefined as File | undefined, previewUrl: undefined as string | undefined, previewFailed: false,
  existing: undefined as { state: 'loading' | 'ready' | 'failed'; url?: string } | undefined,
  pending: 0, message: '', busy: false, online: navigator.onLine, counts: undefined as ReadingCounts | undefined,
  installEvent: undefined as InstallPromptEvent | undefined, installHintHidden: false,
  tab: 'ablesung' as TabId, sortMode: 'standard' as SortMode, prefsApplied: false, updateReady: false, authLogCopied: '', versionCheck: 'checking' as VersionCheck,
  position: undefined as GeoPoint | undefined, points: {} as Record<string, GeoPoint | null>, geoNote: '', shown: PAGE_SIZE,
  ocrHint: undefined as OcrHint | undefined, functionVersion: undefined as string | undefined, showAllVersions: false,
  // Einstellungen
  settings: undefined as SettingsInfo | undefined, settingsList: '', settingsLibrary: '', settingsBusy: false, settingsOcr: 'off' as OcrMode, settingsSite: '', siteText: '',
  newList: '', newLibrary: '', structureNote: undefined as { ok: boolean; text: string } | undefined, consentNeeded: false,
  // Verwaltung
  exportScope: 'versorger' as ExportScope, exportColumns: [...EXPORT_PRESETS.vorlage] as string[], exportStatus: '' as ReadingStatus | '',
  backfillCounts: undefined as OcrBackfillCounts | undefined, backfillRunning: false, backfillMessage: '', importYear: '',
  importDone: undefined as { ok: boolean; text: string } | undefined, importPreview: undefined as { file: File; result: ImportResult } | undefined,
  // Offline-Anzeige
  readiness: undefined as OfflineReadiness | undefined, flyoutOpen: false
};

let backfillStop = false;
let rootElement: HTMLElement;
let handle: ReturnType<typeof mount<ShellState>> | undefined;
let phaseShown: ShellState['phase'] | undefined;
/** Inhalt des gewählten Reiters, schon gebaut; `shellView` setzt ihn in den Rahmen. */
let currentPanel: HTMLElement | undefined;
const shellView = (state: ShellState): HTMLElement => appView(state, currentPanel);
let renderQueued = false;
let ocrSlot: Element | null = null;
let ocrPart: { destroy(): void } | undefined;

// Zähler, mit denen eine ältere, noch laufende Anfrage verworfen wird, sobald eine neuere gestartet ist
const tokens = { counts: 0, ocr: 0, existing: 0, position: 0, coords: 0, readiness: 0 };
let readinessKey = '';
let readinessTick = 0;

const isAdmin = (): boolean => s.user?.roles.some(role => role === appConfig.roles.admin || role === appConfig.roles.settings) ?? false;
const canBackfill = (): boolean => Boolean(s.user?.ocr?.backfill);
const pwaOffline = (): boolean => s.mode === 'pwa';
const embedded = (): boolean => s.mode === 'teams' || s.mode === 'outlook';
/** Sprechende Adresse der installierten PWA (config `pwaUrl`); ohne Eintrag die Adresse, unter der die App gerade läuft. */
const pwaUrl = (): string => `${(appConfig.pwaUrl ?? window.location.origin).replace(/\/$/, '')}/`;
/** Sortierung nach Entfernung gibt es nur in der installierten PWA (in Teams, Outlook und im Browser-Tab ist sie nicht nötig, dort ist der Standort oft gesperrt). */
const distanceSortAvailable = (): boolean => s.mode === 'pwa';
const adminConsentUrl = (): string => `https://login.microsoftonline.com/${appConfig.tenantId}/v2.0/adminconsent?client_id=${appConfig.clientId}&scope=${encodeURIComponent('https://graph.microsoft.com/Sites.Manage.All')}&redirect_uri=${encodeURIComponent(pwaUrl())}`;
const siteOrigin = (): string => { try { return new URL(s.settings?.siteUrl || appConfig.sharePointSiteUrl).origin; } catch { return ''; } };
const hasSavedPhoto = (): boolean => Boolean(s.selected?.Bilddateiname) && s.selected?.Zaehlerstand !== undefined;

function render(): void {
  if (renderQueued) return;
  renderQueued = true;
  queueMicrotask(() => { renderQueued = false; draw(); });
}

function setMessage(text: string): void { s.message = text; render(); }

// --- Zustand für die Ansichten ---------------------------------------------------------------------------------------------------------------------

function tabList(): Array<{ id: TabId; label: string }> {
  return [
    { id: 'ablesung', label: 'Ablesungen' },
    ...(isAdmin() ? [{ id: 'verwaltung' as const, label: 'Verwaltung' }] : []),
    ...(canBackfill() && !isAdmin() ? [{ id: 'auswertung' as const, label: 'Auswertung' }] : []),
    ...(isAdmin() ? [{ id: 'einstellungen' as const, label: 'Einstellungen' }] : []),
    { id: 'info' as const, label: 'Info' }
  ];
}

function visibleItems(distances: Map<string, number>): Reading[] {
  const tokensText = s.query.toLowerCase().split(/\s+/).filter(Boolean);
  const filtered = tokensText.length === 0 ? s.items : s.items.filter(item => { const text = searchText(item); return tokensText.every(token => text.includes(token)); });
  if (s.sortMode !== 'distance' || distances.size === 0) return filtered;
  // Nächste zuerst; Einträge ohne Koordinate bleiben in ihrer bisherigen Reihenfolge am Ende
  return [...filtered].sort((x, y) => (distances.get(x.id) ?? Infinity) - (distances.get(y.id) ?? Infinity));
}

function distanceMap(): Map<string, number> {
  const result = new Map<string, number>();
  if (s.sortMode !== 'distance' || !s.position) return result;
  for (const item of s.items) {
    const point = s.points[addressKey(item)];
    if (point) result.set(item.id, distanceKm(s.position, point));
  }
  return result;
}

let listHasMore = false;

function readingsState(): ReadingsState {
  const distances = distanceMap();
  const visible = visibleItems(distances);
  const shownItems = visible.slice(0, s.shown);
  const hasMore = visible.length > shownItems.length;
  listHasMore = hasMore;
  const countText = s.query.trim()
    ? `${visible.length} von ${s.items.length} Treffern`
    : `${s.items.length} ${s.status === 'Offen' ? 'offene ' : ''}Ablesung${s.items.length === 1 ? '' : 'en'}`;
  const countShown = hasMore ? ` · ${shownItems.length} angezeigt` : '';
  const selected = s.selected;
  const numeric = parseReading(s.value);
  const missing: string[] = [];
  if (!s.photo && !hasSavedPhoto()) missing.push('Foto');
  if (numeric === undefined) missing.push('Zählerstand');
  const unchanged = hasSavedPhoto() && !s.photo && numeric !== undefined && numeric === selected?.Zaehlerstand;
  const hint = s.ocrHint;
  return {
    query: s.query, busy: s.busy, loaded: s.loaded, status: s.status, year: s.year, years: yearOptions(s.year), sortAvailable: distanceSortAvailable(), hideRefresh: pwaOffline(), sortMode: s.sortMode, geoNote: s.geoNote,
    statusFilters: statusFilters.map(filter => ({ value: filter.value, label: filter.label, optionLabel: filterLabel(filter, s.counts), count: filterCount(filter, s.counts) })),
    countLine: (s.loaded ? countText + countShown : ' ') + (s.loaded && s.busy ? ' · wird aktualisiert …' : ''),
    empty: s.loaded && visible.length === 0 && !s.busy,
    cards: shownItems.map(item => ({
      id: item.id, title: item.Strasse || item.Title || '', status: item.Ablesestatus ?? '', badge: badgeClass(item.Ablesestatus), selected: selected?.id === item.id,
      distance: distances.has(item.id) ? formatDistance(distances.get(item.id)!) : undefined,
      ort: item.Ort ?? '', zaehlernummer: item.Zaehlernummer ?? '', ortZaehler: item.OrtZaehler ?? '', zweck: item.Zweck ?? '', hinweise: item.Hinweise ?? '',
      canMarkChecked: isAdmin() && item.Ablesestatus === 'Zu prüfen'
    })),
    more: hasMore ? visible.length - shownItems.length : undefined,
    detail: selected ? {
      title: selected.Strasse || selected.Title || '', ort: selected.Ort ?? '', status: selected.Ablesestatus ?? '', badge: badgeClass(selected.Ablesestatus),
      zaehlernummer: selected.Zaehlernummer ?? '', ortZaehler: selected.OrtZaehler ?? '', zweck: selected.Zweck ?? '', hinweise: selected.Hinweise ?? '',
      unit: zaehlerEinheit(selected.Energie), vorjahr: selected.ZaehlerstandVorjahr === undefined ? '' : formatNumber(selected.ZaehlerstandVorjahr),
      value: s.value, valueInvalid: s.value.trim() !== '' && numeric === undefined,
      belowPrevious: selected.ZaehlerstandVorjahr !== undefined && numeric !== undefined && numeric < selected.ZaehlerstandVorjahr,
      ocr: hint ? {
        status: hint.status, message: hint.message, value: hint.value === undefined ? undefined : formatNumber(hint.value),
        confidence: hint.confidence === undefined ? undefined : String(Math.round(hint.confidence * 100)), testMode: Boolean(s.user?.ocr?.testMode)
      } : undefined,
      photo: s.photo ? { name: s.photo.name, size: (s.photo.size / 1024 / 1024).toFixed(1) } : undefined, previewUrl: s.previewUrl, previewFailed: s.previewFailed,
      existing: hasSavedPhoto() && s.existing ? { state: s.existing.state, url: s.existing.url, info: savedPhotoInfo(selected) } : undefined,
      canSave: Boolean(selected) && missing.length === 0 && !unchanged && !s.busy,
      missingText: missing.length > 0 ? `Fehlt noch: ${missing.join(' und ')}.` : unchanged ? 'Keine Änderung zum Speichern.' : hasSavedPhoto() && !s.photo ? 'Bereit zum Speichern. Das gespeicherte Foto bleibt erhalten.' : 'Bereit zum Speichern.'
    } : undefined
  };
}

function infoState(): InfoState {
  return {
    appName: appConfig.appName, appVersion: appConfig.appVersion, manifestVersion: appConfig.manifestVersion, baselineVersion: appConfig.baselineVersion,
    functionVersion: s.functionVersion, userName: s.user?.name, roles: s.user?.roles, dataSource: s.user?.dataSource, modeLabel: modeLabel(s.mode), mode: s.mode, online: s.online,
    embedded: embedded(), isAdmin: isAdmin(), authLog: getAuthLog(), authLogCopied: s.authLogCopied, releaseNotes, showAllVersions: s.showAllVersions
  };
}

function settingsState(): SettingsState {
  const settings = s.settings;
  let dirty = false;
  if (settings) {
    const savedSite = settings.current.sharePointSiteUrl ?? settings.defaults.sharePointSiteUrl ?? settings.siteUrl;
    dirty = s.settingsSite !== savedSite || s.settingsList !== settings.current.sharePointList || s.settingsLibrary !== settings.current.sharePointPhotoLibrary
      || s.settingsOcr !== (settings.current.ocrMode ?? settings.defaults.ocrMode ?? 'off');
  }
  return {
    busy: s.settingsBusy, settings, siteText: s.siteText, site: s.settingsSite, siteOrigin: siteOrigin(), list: s.settingsList, library: s.settingsLibrary, ocr: s.settingsOcr, dirty,
    newList: s.newList, newLibrary: s.newLibrary, structureNote: s.structureNote, consentNeeded: s.consentNeeded
  };
}

function adminState(): AdminState {
  const columnCount = s.exportScope === 'auswahl' ? s.exportColumns.length : EXPORT_PRESETS[s.exportScope].length;
  const preview = s.importPreview;
  return {
    busy: s.busy, importPreview: preview ? { fileName: preview.file.name, result: preview.result } : undefined, importDone: s.importDone, importYear: s.importYear, yearNow, year: s.year, yearOptions: yearOptions(s.year),
    exportStatus: s.exportStatus, statusOptions: [...statusFilters].reverse().map(filter => ({ value: filter.value, label: filter.value === '' ? 'Alle' : filter.label })),
    exportScope: s.exportScope, scopes: EXPORT_SCOPES.map(({ value, label }) => ({ value, label })), scopeHint: EXPORT_SCOPES.find(scope => scope.value === s.exportScope)?.hint(columnCount) ?? '',
    exportColumns: s.exportColumns, columnChoices: EXPORT_COLUMNS,
    backfill: canBackfill() ? { counts: s.backfillCounts, running: s.backfillRunning, message: s.backfillMessage } : undefined
  };
}

// --- Laden -------------------------------------------------------------------------------------------------------------------------------------------

async function refreshPending(): Promise<void> { s.pending = await queueCount(); render(); }

async function load(): Promise<void> {
  s.busy = true; s.message = ''; render();
  try {
    if (navigator.onLine) {
      const data = await fetchReadings(s.year, s.status || undefined);
      s.items = data;
      if (pwaOffline()) { await cacheReadings(data); rememberCacheInfo(s.year, data.length); }
    } else if (pwaOffline()) {
      const cached = await getCachedReadings(s.year);
      s.items = s.status ? cached.filter(x => x.Ablesestatus === s.status) : cached;
      s.message = 'Offline: Es werden die zuletzt geladenen offenen Ablesungen angezeigt.';
    } else {
      throw new Error('Dieser Zugang ist nur online verfügbar.');
    }
  } catch (error) { s.message = errorText(error); }
  finally { s.busy = false; s.loaded = true; render(); }
  void refreshCounts();
  void updateCoordinates();
}

/** Zahlen für die Statusknöpfe: werden nach jedem Laden der Liste neu geholt (Jahr, Speichern, Synchronisieren, Aktualisieren). */
async function refreshCounts(): Promise<void> {
  const token = ++tokens.counts;
  if (!s.signedIn || !s.online) { s.counts = undefined; render(); return; }
  try { const data = await fetchCounts(s.year); if (token === tokens.counts) { s.counts = data; render(); } }
  catch { if (token === tokens.counts) { s.counts = undefined; render(); } }
}

async function start(): Promise<void> {
  let detected: HostMode = 'browser';
  try { detected = await initializeAuth(); }
  catch (error) { s.message = `Anmeldung konnte nicht gestartet werden: ${errorText(error)}`; }
  s.mode = detected; s.ready = true; render();
  if (embedded()) { try { teams.app.notifyAppLoaded(); teams.app.notifySuccess(); } catch { /* Host ohne Unterstuetzung */ } }
  void checkVersionBeforeSignIn();
  // Zuletzt gewählte Sortierung wiederherstellen und Auswahl merken
  if (distanceSortAvailable() && readPrefs().sortMode === 'distance') { s.sortMode = 'distance'; void startPosition(); }
  s.prefsApplied = true; persistPrefs();
  if (isSignedIn()) {
    // Reiter und Ablesungen sofort starten, ohne auf /me zu warten: Die Rollen kommen zuerst aus dem Zwischenspeicher dieses Kontos oder aus dem ID-Token,
    // /me bestätigt sie danach (der Server prüft immer). So erscheinen die Reiter zusammen mit der App und nicht erst nach dem Laden.
    const account = getAccountSummary();
    try {
      const cached = JSON.parse(localStorage.getItem('pv-user') ?? 'null') as (UserInfo & { accountId?: string }) | null;
      if (cached && (!account || cached.accountId === account.id)) s.user = cached;
      else if (account && account.roles.length > 0) s.user = { name: account.name, roles: account.roles };
    } catch { /* kein brauchbarer Zwischenspeicher */ }
    s.signedIn = true; render();
    void load();
    try {
      if (!navigator.onLine && detected === 'pwa') {
        if (!localStorage.getItem('pv-user')) throw new Error('Für den ersten Start ist eine Online-Anmeldung erforderlich.');
      } else {
        const currentUser = await getMe();
        localStorage.setItem('pv-user', JSON.stringify({ ...currentUser, accountId: account?.id }));
        s.user = currentUser; render();
      }
    } catch (e) { s.message = errorText(e); s.loaded = true; render(); }
  }
  if (detected === 'pwa') await refreshPending();
  render();
}

/** Vor der Anmeldung nach einer neuen Version suchen: Die installierte PWA startet zuerst mit dem gespeicherten Stand. Ist eine neuere Version da, wird sie geladen
 *  (die Seite lädt dann neu), bevor man sich anmelden kann; so wird die Anmeldung nicht von einem späteren Neuladen unterbrochen. */
async function checkVersionBeforeSignIn(): Promise<void> {
  const finish = () => { s.versionCheck = 'done'; render(); };
  if (isSignedIn() || !('serviceWorker' in navigator)) { finish(); return; }
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration) { finish(); return; }
    await Promise.race([registration.update(), new Promise(resolve => setTimeout(resolve, 5000))]);
    if (registration.installing || registration.waiting) { s.versionCheck = 'updating'; render(); setTimeout(finish, 15000); } else finish();
  } catch { finish(); }
}

function persistPrefs(): void {
  if (!s.prefsApplied) return;
  writePrefs({ status: s.status, sortMode: distanceSortAvailable() ? s.sortMode : readPrefs().sortMode });
}

// --- Auswahl, Foto, Erkennung ------------------------------------------------------------------------------------------------------------------------

function releasePreview(): void { if (s.previewUrl) URL.revokeObjectURL(s.previewUrl); s.previewUrl = undefined; }
function releaseExisting(): void { if (s.existing?.url) URL.revokeObjectURL(s.existing.url); s.existing = undefined; }

/** Gespeichertes Foto einer schon abgelesenen Ablesung für die Detailansicht laden. */
function loadExistingPhoto(): void {
  const token = ++tokens.existing;
  releaseExisting();
  const item = s.selected;
  if (!item || !item.Bilddateiname || item.Zaehlerstand === undefined) return;
  s.existing = { state: 'loading' };
  fetchReadingPhoto(item.id)
    .then(url => { if (token !== tokens.existing) URL.revokeObjectURL(url); else { s.existing = { state: 'ready', url }; render(); } })
    .catch(() => { if (token === tokens.existing) { s.existing = { state: 'failed' }; render(); } });
}

function setSelected(item: Reading | undefined): void {
  s.selected = item;
  loadExistingPhoto();
  render();
}

function select(item: Reading): void {
  s.value = item.Zaehlerstand?.toString().replace('.', ',') ?? '';
  setPhoto(undefined);
  s.message = '';
  setSelected(item);
  window.scrollTo({ top: 0 });
}

function setPhoto(file: File | undefined): void {
  releasePreview();
  s.photo = file; s.previewFailed = false;
  if (file) s.previewUrl = URL.createObjectURL(file);
  updateOcr();
  render();
}

/** Zählerstand-Erkennung: Vorschlag aus dem Foto (nur online und wenn sie für den Nutzer gilt). */
function updateOcr(): void {
  const token = ++tokens.ocr;
  s.ocrHint = undefined;
  const photo = s.photo;
  if (!photo || !s.user?.ocr?.available || !s.online) return;
  s.ocrHint = { status: 'loading' };
  void (async () => {
    try {
      const result = await readMeterOcr(await normalizePhoto(photo));
      if (token === tokens.ocr) { s.ocrHint = { status: 'done', value: result.meterReading, confidence: result.confidence }; render(); }
    } catch (error) {
      if (token === tokens.ocr) { s.ocrHint = { status: 'error', message: errorText(error) }; render(); }
    }
  })();
}

async function save(): Promise<void> {
  const selected = s.selected;
  const numeric = parseReading(s.value);
  if (!selected || (!s.photo && !hasSavedPhoto())) return setMessage('Bitte Zählerstand und Foto erfassen.');
  if (numeric === undefined) return setMessage('Bitte einen gültigen Zählerstand eingeben.');
  s.busy = true; s.message = s.photo ? 'Foto wird vorbereitet …' : 'Zählerstand wird gespeichert …'; render();
  try {
    const normalized = s.photo ? await normalizePhoto(s.photo) : undefined;
    const queueItem: QueueItem = {
      localId: crypto.randomUUID(), readingId: selected.id, importId: selected.ImportID,
      importYear: selected.Importjahr ?? s.year, meterNumber: selected.Zaehlernummer ?? 'unbekannt',
      street: selected.Strasse ?? '', value: numeric, previousValue: selected.ZaehlerstandVorjahr,
      capturedAt: new Date().toISOString(), capturedOffline: !navigator.onLine,
      photo: normalized, attempts: 0
    };
    if (pwaOffline()) {
      await enqueue(queueItem);
      await refreshPending();
      s.message = 'Lokal gespeichert. Bitte mit „Jetzt synchronisieren“ übertragen.';
    } else {
      if (!navigator.onLine) throw new Error('Dieser Zugang ist nur online verfügbar.');
      await submitReading(queueItem);
      s.message = 'Ablesung erfolgreich übertragen.';
      await load();
    }
    setSelected(undefined); setPhoto(undefined); s.value = '';
  } catch (error) { s.message = errorText(error); }
  finally { s.busy = false; render(); }
}

async function doSync(): Promise<void> {
  s.busy = true; s.message = 'Synchronisierung läuft …'; render();
  try {
    const result = await synchronize((done, total) => setMessage(`Synchronisierung ${done}/${total} …`));
    await refreshPending(); await load();
    s.message = `${result.sent} Ablesung(en) übertragen, ${result.failed} fehlgeschlagen.${result.errors.length ? ' ' + result.errors.join(' | ') : ''}`;
  } catch (error) { s.message = errorText(error); }
  finally { s.busy = false; render(); }
}

/** Die installierte App lässt sich nicht herunterziehen: Knopf neben dem Status sucht eine neue Version und lädt die Seite neu (die Offline-Warteschlange bleibt auf dem Gerät). */
async function reloadApp(): Promise<void> {
  const hasInput = Boolean(s.photo) || (Boolean(s.selected) && s.value !== '' && parseReading(s.value) !== s.selected?.Zaehlerstand);
  if (hasInput && !window.confirm('Es gibt noch nicht gespeicherte Eingaben. Trotzdem neu laden?')) return;
  try { const registration = await navigator.serviceWorker?.getRegistration(); await registration?.update(); } catch { /* ohne Netz genügt das Neuladen */ }
  window.location.reload();
}

// --- Entfernung --------------------------------------------------------------------------------------------------------------------------------------

/** Standort des Geräts bestimmen ... */
function startPosition(): void {
  const token = ++tokens.position;
  if (s.sortMode !== 'distance') return;
  s.geoNote = 'Standort wird ermittelt …'; render();
  getPosition().then(
    found => { if (token === tokens.position) { s.position = found; s.geoNote = ''; render(); void updateCoordinates(); } },
    error => { if (token === tokens.position) { s.geoNote = errorText(error); s.sortMode = 'standard'; persistPrefs(); render(); } }
  );
}

/** ... und die Koordinaten der Adressen holen (zuerst aus dem Gerätespeicher, fehlende bei Azure Maps). */
async function updateCoordinates(): Promise<void> {
  const token = ++tokens.coords;
  if (s.sortMode !== 'distance' || !s.position || s.items.length === 0) return;
  try {
    const result = await resolveCoordinates(s.items, navigator.onLine, open => { if (token === tokens.coords) { s.geoNote = open > 0 ? `Adressen werden ermittelt (${open}) …` : ''; render(); } });
    if (token !== tokens.coords) return;
    s.points = result.points;
    s.geoNote = result.missingOffline > 0 ? `Offline: für ${result.missingOffline} Adresse(n) ist noch kein Standort gespeichert, sie stehen am Ende.` : '';
  } catch (error) { if (token === tokens.coords) s.geoNote = errorText(error); }
  render();
}

// --- Einstellungen und Verwaltung --------------------------------------------------------------------------------------------------------------------

/** `site`: Adresse einer anderen Website; deren Listen und Bibliotheken werden geladen, die Auswahl beginnt dann leer. */
async function loadSettings(site?: string): Promise<void> {
  s.settingsBusy = true; render();
  try {
    const data = await getSettings(site || undefined);
    s.settings = data;
    s.settingsSite = data.siteUrl;
    s.siteText = siteToInput(data.siteUrl);
    const savedSite = data.current.sharePointSiteUrl ?? data.defaults.sharePointSiteUrl ?? data.siteUrl;
    const sameSite = data.siteUrl === savedSite;
    s.settingsList = sameSite ? data.current.sharePointList : '';
    s.settingsLibrary = sameSite ? data.current.sharePointPhotoLibrary : '';
    s.settingsOcr = data.current.ocrMode ?? data.defaults.ocrMode ?? 'off';
  } catch (error) { s.message = errorText(error); }
  finally { s.settingsBusy = false; render(); }
}

async function storeSettings(): Promise<void> {
  s.settingsBusy = true; s.message = ''; render();
  try {
    await saveSettings(s.settingsList, s.settingsLibrary, s.settingsOcr, s.settingsSite || undefined);
    await loadSettings();
    await load();   // löscht die Meldung: die Bestätigung kommt deshalb erst danach (und bleibt bis zum Reiterwechsel stehen)
    const saved = 'Einstellungen gespeichert. Sie gelten ab sofort für alle Nutzer (spätestens nach einer Minute).';
    s.message = s.message ? `${saved}\n${s.message}` : saved;
  } catch (error) { s.message = errorText(error); }
  finally { s.settingsBusy = false; render(); }
}

/** Legt Liste und Bibliothek mit den gewählten Namen an (oder ergänzt fehlende Spalten) und wählt sie danach aus. */
async function makeStructure(): Promise<void> {
  s.settingsBusy = true; s.structureNote = undefined; render();
  try {
    s.consentNeeded = false;
    const result = await createStructure(s.newList.trim(), s.newLibrary.trim(), s.settingsSite || undefined);
    const describe = (kind: string, r: { created: boolean; added: string[] }) => r.created ? `${kind} angelegt` : r.added.length ? `${kind} vorhanden, ${r.added.length} Spalte(n) ergänzt` : `${kind} vorhanden und vollständig`;
    s.settings = await getSettings(s.settingsSite || undefined);
    s.settingsList = s.newList.trim(); s.settingsLibrary = s.newLibrary.trim();
    s.structureNote = { ok: true, text: `${describe('Liste', result.list)}, ${describe('Bibliothek', result.library)}. Beide sind oben ausgewählt; mit „Einstellungen speichern“ gelten sie für alle Nutzer.` };
  } catch (error) {
    if (error instanceof ConsentRequiredError) s.consentNeeded = true;
    s.structureNote = { ok: false, text: errorText(error) };
  }
  finally { s.settingsBusy = false; render(); }
}

async function loadBackfill(): Promise<void> {
  if (!canBackfill()) return;
  try { s.backfillCounts = await ocrBackfillStatus(s.year); }
  catch (error) { s.backfillMessage = errorText(error); }
  render();
}

/** Fotos nachträglich auswerten, Schritt für Schritt, bis nichts mehr offen ist. Der Free-Tarif von Azure erlaubt 20 Aufrufe pro Minute; deshalb wenige Fotos je Schritt mit Pause. */
async function runBackfill(mode: 'missing' | 'recompute'): Promise<void> {
  backfillStop = false; s.backfillRunning = true; s.backfillMessage = ''; render();
  let skip = 0; let updated = 0;
  try {
    while (!backfillStop) {
      const step = await ocrBackfillRun(s.year, mode, skip);
      updated += step.updated; skip += step.errors.length;
      s.backfillMessage = `${updated} ausgewertet, ${step.remaining} offen${step.errors.length ? `, Fehler: ${step.errors.join(' | ')}` : ''}.`; render();
      await loadBackfill();
      if (step.rateLimited) { s.backfillMessage = `${s.backfillMessage} Azure-Grenze erreicht, warte 60 Sekunden …`; render(); await new Promise(resolve => setTimeout(resolve, 60000)); continue; }
      if (step.remaining <= 0 || step.processed === 0) break;
      if (mode === 'missing') await new Promise(resolve => setTimeout(resolve, 15000));
    }
    s.backfillMessage = `${backfillStop ? 'Angehalten. ' : 'Fertig. '}${s.backfillMessage}`;
  } catch (error) { s.backfillMessage = errorText(error); }
  finally { s.backfillRunning = false; await loadBackfill(); }
}

async function runExport(format: 'xlsx' | 'csv'): Promise<void> {
  s.busy = true; render();
  try { await downloadExport(s.year, format, { columns: s.exportScope === 'auswahl' ? s.exportColumns.join(',') : s.exportScope, status: s.exportStatus || undefined }); }
  catch (error) { s.message = errorText(error); }
  finally { s.busy = false; render(); }
}

/** Schritt 1: Datei prüfen lassen und die Vorschau zeigen, es wird noch nichts angelegt. */
async function previewImport(file?: File, yearText = s.importYear): Promise<void> {
  if (!file) return;
  s.busy = true; s.importPreview = undefined; s.importDone = undefined; render();
  try { s.importPreview = { file, result: await importFile(file, true, Number(yearText) || undefined) }; }
  catch (error) { s.importDone = { ok: false, text: errorText(error) }; }
  finally { s.busy = false; render(); }
}

/** Schritt 2: nach der Bestätigung wirklich importieren. */
async function confirmImport(): Promise<void> {
  if (!s.importPreview) return;
  s.busy = true; render();
  try {
    const result = await importFile(s.importPreview.file, false, Number(s.importYear) || undefined);
    s.importPreview = undefined;
    s.importDone = { ok: result.errors.length === 0, text: `Import abgeschlossen: ${result.created} Auftrag/Aufträge neu angelegt, ${result.skipped} übersprungen (schon vorhanden oder doppelt), ${result.errors.length} fehlerhaft.${result.errors.length ? ' ' + result.errors.join(' | ') : ''}` };
    await load();
  } catch (error) { s.importDone = { ok: false, text: `Der Import ist fehlgeschlagen: ${errorText(error)}` }; }
  finally { s.busy = false; render(); }
}

// --- Links, Installation, Protokoll ------------------------------------------------------------------------------------------------------------------

/** Öffnet eine Adresse im Browser (in Teams/Outlook über den Host); sonst wird sie kopiert. */
async function openExternal(url: string): Promise<void> {
  if (embedded()) { try { await teams.app.openLink(url); return; } catch { /* Ersatzweg unten */ } }
  const opened = window.open(url, '_blank');
  if (opened) { opened.opener = null; return; }
  try { await navigator.clipboard.writeText(url); setMessage('Der Link wurde in die Zwischenablage kopiert. Bitte im Browser einfügen und öffnen.'); }
  catch { setMessage(`Bitte diese Adresse im Browser öffnen: ${url}`); }
}

async function copyLink(): Promise<void> {
  const url = pwaUrl();
  try { await navigator.clipboard.writeText(url); setMessage(`Link in die Zwischenablage kopiert: ${url}`); return; } catch { /* Ersatzweg unten */ }
  try {
    const area = document.createElement('textarea');
    area.value = url; area.setAttribute('readonly', ''); area.style.position = 'fixed'; area.style.opacity = '0';
    document.body.appendChild(area); area.select();
    const copied = document.execCommand('copy');
    document.body.removeChild(area);
    if (copied) { setMessage(`Link in die Zwischenablage kopiert: ${url}`); return; }
  } catch { /* Hinweis unten */ }
  setMessage(`Der Link konnte nicht automatisch kopiert werden. Bitte manuell kopieren: ${url}`);
}

async function openPwa(): Promise<void> {
  const url = pwaUrl();
  if (embedded()) { try { await teams.app.openLink(url); return; } catch { /* Ersatzweg: normales Öffnen im Browser */ } }
  // Ohne „noopener“, damit ein blockiertes Fenster (null) erkennbar ist; Opener danach trennen.
  const opened = window.open(url, '_blank');
  if (opened) { opened.opener = null; return; }
  // Outlook blockiert Popups und openLink teils still: Link kopieren und Hinweis zeigen.
  await copyLink();
  setMessage(`Outlook konnte den Link nicht öffnen. ${s.message ?? ''} Bitte im Browser einfügen und öffnen.`.trim());
}

async function installPwa(): Promise<void> {
  if (s.installEvent) {
    await s.installEvent.prompt();
    const choice = await s.installEvent.userChoice;
    if (choice.outcome === 'accepted') { s.installEvent = undefined; render(); }
    return;
  }
  setMessage(isIos()
    ? 'iPhone/iPad: Im Safari auf „Teilen“ tippen und „Zum Home-Bildschirm“ wählen.'
    : 'Installation: Im Browser-Menü „App installieren“ bzw. „Zum Startbildschirm hinzufügen“ wählen.');
}

async function copyAuthLog(): Promise<void> {
  const text = getAuthLog().map(entry => `${new Date(entry.at).toLocaleString('de-DE')}: ${entry.event}${entry.detail ? ` (${entry.detail})` : ''}`).reverse().join('\n');
  try { await navigator.clipboard.writeText(text); s.authLogCopied = 'Protokoll in die Zwischenablage kopiert.'; }
  catch { s.authLogCopied = 'Kopieren nicht möglich. Bitte ein Bildschirmfoto des Protokolls verwenden.'; }
  render();
}

// --- Reiter ------------------------------------------------------------------------------------------------------------------------------------------

function setTab(tab: TabId): void {
  s.tab = tab;
  s.message = '';   // Eine Meldung gehört zu ihrem Reiter: Beim Wechsel verschwindet sie, statt in jeder Ansicht stehen zu bleiben.
  render();
  if (tab === 'einstellungen' && isAdmin() && !s.settings) void loadSettings();
  if (tab === 'auswertung' || tab === 'verwaltung') void loadBackfill();
  if (tab === 'info') void loadFunctionVersion();
}

/** Version der Azure Function (Info-Reiter): anonymer Gesundheitstest, nur wenn der Reiter offen und online ist. */
async function loadFunctionVersion(): Promise<void> {
  if (s.tab !== 'info' || !s.online) return;
  try {
    const body = await (await fetch(`${appConfig.functionUrl}/ping`)).json() as { version?: string };
    s.functionVersion = body.version; render();
  } catch { /* ohne Versionsangabe */ }
}

function resetList(): void { s.shown = PAGE_SIZE; }

function setStatus(value: ReadingStatus | ''): void { s.status = value; resetList(); persistPrefs(); void load(); }
function setYear(year: number): void { s.year = year; resetList(); if (s.tab === 'auswertung' || s.tab === 'verwaltung') void loadBackfill(); void load(); }

const readingsActionMap: ReadingsActions = {
  setQuery: value => { s.query = value; resetList(); render(); },
  refresh: () => { void load(); if (s.sortMode === 'distance') void startPosition(); },
  setStatus, setYear,
  setSort: mode => { s.geoNote = ''; s.sortMode = mode; resetList(); persistPrefs(); if (mode === 'distance') void startPosition(); else { tokens.position += 1; tokens.coords += 1; } render(); },
  select: id => { const item = s.items.find(entry => entry.id === id); if (item) select(item); },
  markChecked: id => void (async () => { await markChecked(id); await load(); })(),
  showMore: () => { s.shown += PAGE_SIZE; render(); },
  back: () => setSelected(undefined),
  setValue: value => { s.value = value; render(); },
  applyOcr: () => { if (s.ocrHint?.value !== undefined) { s.value = String(s.ocrHint.value).replace('.', ','); render(); } },
  setPhoto, previewFailed: () => { s.previewFailed = true; render(); }, save: () => void save()
};

const infoActionMap: InfoActions = {
  installPwa: () => void installPwa(), signOut: () => void signOut(), openPwa: () => void openPwa(), copyLink: () => void copyLink(),
  copyAuthLog: () => void copyAuthLog(), showAllVersions: () => { s.showAllVersions = true; render(); }
};

const settingsActionMap: SettingsActions = {
  reload: () => void loadSettings(s.settingsSite),
  setSiteText: value => { s.siteText = value; s.settingsSite = siteFromInput(value, siteOrigin()); render(); },
  setList: value => { s.settingsList = value; render(); }, setLibrary: value => { s.settingsLibrary = value; render(); }, setOcr: value => { s.settingsOcr = value; render(); },
  setNewList: value => { s.newList = value; render(); }, setNewLibrary: value => { s.newLibrary = value; render(); },
  makeStructure: () => void makeStructure(), openConsent: () => void openExternal(adminConsentUrl()), save: () => void storeSettings(),
  reset: () => { if (s.settings) { s.settingsList = s.settings.defaults.sharePointList; s.settingsLibrary = s.settings.defaults.sharePointPhotoLibrary; s.settingsOcr = s.settings.defaults.ocrMode ?? 'off'; render(); } }
};

const adminActionMap: AdminActions = {
  pickImportFile: file => void previewImport(file),
  setImportYear: value => { s.importYear = value; render(); if (s.importPreview) void previewImport(s.importPreview.file, value); },
  confirmImport: () => void confirmImport(), cancelImport: () => { s.importPreview = undefined; render(); }, setYear,
  setExportStatus: value => { s.exportStatus = value as ReadingStatus | ''; render(); }, setExportScope: value => { s.exportScope = value as ExportScope; render(); },
  toggleColumn: (key, on) => { s.exportColumns = on ? [...s.exportColumns, key] : s.exportColumns.filter(entry => entry !== key); render(); },
  selectAllColumns: () => { s.exportColumns = EXPORT_COLUMNS.map(([, key]) => key); render(); }, clearColumns: () => { s.exportColumns = []; render(); },
  versorgerColumns: () => { s.exportColumns = [...EXPORT_PRESETS.versorger]; render(); },
  runExport: format => void runExport(format), runBackfill: mode => void runBackfill(mode), stopBackfill: () => { backfillStop = true; }
};

const shellActions: Actions = {
  'app-tab': el => setTab(el.getAttribute('data-value') as TabId),
  'app-sync': () => void doSync(),
  'app-sign-in': () => { if (isSigningIn()) return; void signIn().catch(error => setMessage(errorText(error))); render(); },
  'app-sign-in-other': () => { if (isSigningIn()) return; void signIn(true).catch(error => setMessage(errorText(error))); render(); },
  'app-reload': () => void reloadApp(),
  'app-reload-now': () => window.location.reload(),
  'app-flyout': () => { s.flyoutOpen = !s.flyoutOpen; render(); },
  'app-copy-link': () => void copyLink(),
  'app-hide-hint': () => { s.installHintHidden = true; try { localStorage.setItem(INSTALL_HINT_KEY, '1'); } catch { /* Speicher gesperrt: Hinweis kommt beim nächsten Start wieder */ } render(); }
};

// --- Zeichnen ----------------------------------------------------------------------------------------------------------------------------------------

function shellState(): ShellState {
  const phase = !s.ready ? 'starting' : !isSignedIn() ? 'signin' : 'app';
  return {
    phase, mode: s.mode, embedded: embedded(), pwaOffline: pwaOffline(), loaded: s.loaded, busy: s.busy, online: s.online, pending: s.pending,
    message: s.message, authError: getAuthError(), signingIn: isSigningIn(), rememberedAccount: getRememberedAccountName(), versionCheck: s.versionCheck, updateReady: s.updateReady, hasSelection: Boolean(s.selected),
    installHint: s.mode === 'browser' && !s.installHintHidden && isSafariIos(), tab: s.tab, tabs: tabList(), readiness: s.readiness, flyoutOpen: s.flyoutOpen, pwaUrl: pwaUrl()
  };
}

/** Inhalt des gewählten Reiters samt Zuordnung der Ereignisse. Ein Reiter ohne Berechtigung zeigt nichts. */
function panel(state: ShellState): { element?: HTMLElement; actions: Actions } {
  if (state.tab === 'ablesung') return { element: readingsView(readingsState()), actions: readingsActions(readingsActionMap) };
  if (state.tab === 'verwaltung' && isAdmin()) return { element: adminView(adminState()), actions: adminActions(adminActionMap) };
  if (state.tab === 'auswertung' && canBackfill() && !isAdmin()) {
    const admin = adminState();
    return { element: admin.backfill ? auswertungView({ ...admin, backfill: admin.backfill }) : undefined, actions: adminActions(adminActionMap) };
  }
  if (state.tab === 'einstellungen' && isAdmin()) return { element: settingsView(settingsState()), actions: settingsActions(settingsActionMap) };
  if (state.tab === 'info') return { element: infoView(infoState()), actions: infoActions(infoActionMap) };
  return { actions: {} };
}

function draw(): void {
  const state = shellState();
  if (state.phase === 'app' && !state.tabs.some(t => t.id === state.tab)) s.tab = state.tab = 'ablesung';
  const part = state.phase === 'app' ? panel(state) : { element: undefined, actions: {} as Actions };
  if (phaseShown !== state.phase) { handle?.destroy(); handle = mount(rootElement, shellView, shellActions); phaseShown = state.phase; }
  currentPanel = part.element;
  handle!.update(state, { ...shellActions, ...part.actions });
  afterDraw();
}

// Dinge, die erst nach dem Zeichnen möglich sind: Platz für „Erkennung verbessern“, Höhe der Kopfzeile, Ende der Liste, Offline-Anzeige
let topbarObserved: Element | null = null;
let topbarObserver: ResizeObserver | undefined;
let listEndObserved: Element | null = null;
let listObserver: IntersectionObserver | undefined;

function afterDraw(): void {
  // „Erkennung verbessern“ steht im Platzhalter der Verwaltung und hat einen eigenen Zustand
  const slot = document.querySelector('[data-slot="ocr-tests"]');
  if (slot !== ocrSlot) {
    ocrPart?.destroy(); ocrPart = undefined; ocrSlot = slot;
    if (slot) {
      ocrPart = createOcrTests(slot as HTMLElement, {
        api: { fetchOcrTests, createOcrTest, deleteOcrTest, fetchOcrTestPhoto, setupOcrTests },
        isConsentRequired: error => error instanceof ConsentRequiredError,
        normalizePhoto, siteFromInput, siteToInput, consentUrl: adminConsentUrl(),
        openLink: url => openExternal(url), confirm: text => window.confirm(text), download
      });
    }
  }
  // Höhe der festen Kopfzeile (Marke und Reiter) als --topbar-hoehe: Darunter bleibt die Liste links stehen (.layout setzt --sticky-oben der Baseline darauf)
  const topbar = document.querySelector<HTMLElement>('.topbar');
  if (topbar !== topbarObserved) {
    topbarObserver?.disconnect(); topbarObserved = topbar;
    if (topbar) {
      const measure = () => document.documentElement.style.setProperty('--topbar-hoehe', `${Math.ceil(topbar.getBoundingClientRect().height)}px`);
      measure();
      topbarObserver = new ResizeObserver(measure);
      topbarObserver.observe(topbar);
    }
  }
  // Beim Herunterscrollen weitere Karten zeigen: beobachtet das Ende der Liste (`.list-more`)
  const listEnd = s.tab === 'ablesung' && listHasMore ? document.querySelector('.list-more') : null;
  if (listEnd !== listEndObserved) {
    listObserver?.disconnect(); listEndObserved = listEnd;
    if (listEnd) {
      listObserver = new IntersectionObserver(entries => { if (entries.some(entry => entry.isIntersecting)) { s.shown += PAGE_SIZE; render(); } }, { rootMargin: '300px' });
      listObserver.observe(listEnd);
    }
  }
  fitFlyout();
  updateReadiness();
}

/** Das Statusfenster hängt unter der Kopfzeile: Höhe auf den sichtbaren Rest begrenzen, damit es nicht über den Bildschirmrand ragt (es scrollt dann selbst). */
function fitFlyout(): void {
  const panelElement = document.getElementById('offline-panel');
  if (!panelElement) return;
  const top = panelElement.getBoundingClientRect().top;
  panelElement.style.setProperty('--flyout-max', `${Math.max(160, Math.floor(window.innerHeight - top - 12))}px`);
}

/** Berechnet die Offline-Bereitschaft neu, wenn sich Netz, Daten, Warteschlange oder Service Worker ändern. */
function updateReadiness(): void {
  if (!s.ready || !pwaOffline()) return;
  const key = [s.mode, s.year, isSignedIn(), s.busy, s.pending, s.online, readinessTick].join('|');
  if (key === readinessKey) return;
  readinessKey = key;
  const token = ++tokens.readiness;
  checkOfflineReadiness({ mode: s.mode, year: s.year, signedIn: isSignedIn() })
    .then(result => { if (token === tokens.readiness) { s.readiness = result; render(); } })
    .catch(() => undefined);
}

// --- Start -------------------------------------------------------------------------------------------------------------------------------------------

function listen(): void {
  window.addEventListener('online', () => { s.online = true; void refreshCounts(); updateOcr(); void loadFunctionVersion(); render(); });
  window.addEventListener('offline', () => { s.online = false; updateOcr(); render(); });
  window.addEventListener('beforeinstallprompt', event => { event.preventDefault(); s.installEvent = event as InstallPromptEvent; });
  window.addEventListener('resize', fitFlyout);
  window.addEventListener('scroll', fitFlyout, true);
  document.addEventListener('keydown', event => { if (event.key === 'Escape' && s.flyoutOpen) { s.flyoutOpen = false; render(); } });
  // Ein Klick (oder Tippen) irgendwo außerhalb von Schaltfläche und Fenster schließt das Statusfenster.
  document.addEventListener('pointerdown', event => {
    if (!s.flyoutOpen) return;
    const target = event.target as Node;
    if (document.getElementById('offline-panel')?.contains(target) || document.querySelector('.brand .wb-live')?.contains(target)) return;
    s.flyoutOpen = false; render();
  });
  // Neue Version: Sobald der neue Service Worker die Seite übernimmt, wird einmal neu geladen (vorher lief nach dem Start noch die alte Version).
  // Wird gerade etwas erfasst, erscheint stattdessen ein Hinweis, damit nichts verloren geht. Beim Zurückkehren in die App wird nach Updates gesucht.
  if (!('serviceWorker' in navigator)) return;
  let controlled = Boolean(navigator.serviceWorker.controller);
  let reloaded = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    readinessTick += 1; render();
    if (!controlled) { controlled = true; return; } // erste Installation: die Seite ist schon aktuell
    if (reloaded) return;
    if (isLoginInProgress()) return; // Ein Neuladen mitten in der Anmeldung verwirft deren Zwischenstand: die neue Version gilt beim nächsten Start
    if (Boolean(s.selected) || Boolean(s.photo) || s.value !== '' || s.busy) { s.updateReady = true; render(); return; }
    reloaded = true;
    window.location.reload();
  });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    void navigator.serviceWorker.getRegistration().then(registration => registration?.update()).catch(() => undefined);
  });
}

export function startApp(root: HTMLElement): void {
  rootElement = root;
  try { s.installHintHidden = localStorage.getItem(INSTALL_HINT_KEY) === '1'; } catch { /* Hinweis bleibt sichtbar */ }
  listen();
  draw();
  void start();
}
